<template>
  <div>
    <div class="lg:px-4 py-8 lg:py-16 flex flex-col items-center relative">
      <TContainer
        size="xl"
        class="w-full lg:px-16 relative z-10"
      >
        <AppHeaderLogo
          :to="localePath({ name: 'index' })"
          class="lg:hidden mb-4 self-center"
        />

        <THeading
          :level="1"
          class="mb-2"
         
        >
          {{ title }}
        </THeading>

        <p class="lg:mb-8 font-semibold">
          {{ t('subtitle') }}
        </p>

        <div
          ref="searchFormEl"
          class="relative lg:h-15"
        >
          <AtomTransition timing="fast">
            <div
              v-if="!hydrationStatus"
              key="loader"
              class="absolute h-full w-full rounded-full bg-gray-100 animate-pulse"
            />
            <SearchFormBar
              v-else
              key="content"
              class="max-lg:hidden"
              has-rental-type
            />
          </AtomTransition>
        </div>

        <slot />
      </TContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  rvTitle: string
  locationTitle: string
  locationName: string
  keepCurrentSeoContent?: boolean
}>()

const hydrationStatus = useHydrationStatus()
const localePath = useLocalePath()
const { locale, t } = useI18n()
const { isMobile } = useDevice()

const title = computed(() => {
  if (props.keepCurrentSeoContent) {
    return capitalizeFirstLetter(t('title', { rvTitle: props.rvTitle, location: locale.value === 'fr' ? props.locationTitle : props.locationName }))
  }

  const isGenericRv = props.rvTitle === t('common.rv') || props.rvTitle === t('common.rvs')
  const rvTitle = isGenericRv ? t('camper') : props.rvTitle

  return capitalizeFirstLetter(t('destinationTitle', { rvTitle, location: props.locationTitle }))
})

const searchFormEl = useTemplateRef('searchFormEl')

const stickyHeaderSearchFormShown = useStickyHeaderSearchFormShown()

useIntersectionObserver(searchFormEl, (entries) => {
  const entry = entries.at(-1)

  // On navigation the incoming page is mounted in a detached container, where
  // the observer reports "not intersecting". Only act on real measurements.
  if (!entry?.target.isConnected) {
    return
  }

  stickyHeaderSearchFormShown.value = !entry.isIntersecting
}, { rootMargin: isMobile ? '0px' : '-100px' })

onBeforeMount(() => {
  stickyHeaderSearchFormShown.value = false
})
</script>

<i18n lang="json">
{
  "en": {
    "title": "{rvTitle} rentals {location}",
    "camper": "RV & camper",
    "destinationTitle": "{rvTitle} rentals {location}",
    "subtitle": "Discover the RVezy RV rental marketplace"
  },
  "fr": {
    "title": "{rvTitle} à louer {location}",
    "camper": "VR & campervan",
    "destinationTitle": "{rvTitle} à louer {location}",
    "subtitle": "Découvrez la plateforme de location de VRs entre particuliers RVezy"
  }
}
</i18n>
